import React, { useEffect, useState } from 'react';
import { motion } from 'motion/react';
import { ArrowRight, Menu, X } from 'lucide-react';
import { Dropdown } from 'antd';
import { useLocation, useNavigate } from 'react-router-dom';
import { getblobLink } from '../services/helper';
import { useDispatch } from 'react-redux';
import authActions from '../actions/authActions';

// ============================================
// NAVBAR
// ============================================
const Navbar = () => {

    const [isOpen, setIsOpen] = React.useState(false);
    const location = useLocation();
    const navigate = useNavigate();
    const [activeSection, setActiveSection] = useState<string>('');
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [authUser, setAuthUser] = useState<any>(null);
    const profileMenuRef = React.useRef<HTMLDivElement | null>(null);
    const isLandingRoute = location?.pathname === '/';
    const isPricingRoute = location?.pathname === '/pricing';
    const isDocRoute     = !isLandingRoute && !isPricingRoute;
    const dispatch = useDispatch();

    // Check authentication status
    useEffect(() => {
        const checkAuth = () => {
            const authData = localStorage.getItem('AUTHDATA');
            setIsAuthenticated(!!authData);
            if (authData) {
                try {
                    const parsed = JSON.parse(authData);
                    setAuthUser(parsed || null);
                } catch (e) {
                    setAuthUser(null);
                }
            } else {
                setAuthUser(null);
            }
        };

        checkAuth();

        window.addEventListener('storage', checkAuth);
        window.addEventListener('authStateChanged', checkAuth);

        const onDocClick = (e: MouseEvent) => {
            if (!profileMenuRef.current) return;
            if (e.target instanceof Node && profileMenuRef.current.contains(e.target)) return;
        };

        document.addEventListener('click', onDocClick);
        return () => {
            window.removeEventListener('storage', checkAuth);
            window.removeEventListener('authStateChanged', checkAuth);
            document.removeEventListener('click', onDocClick);
        };
    }, [location.pathname]);

    const sectionIds = ['hero', 'features', 'mobile-app', 'about', 'contact'];
    const isManualScrollRef = React.useRef(false);
    const manualScrollTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

    const scrollToSection = (sectionId: string) => {
        setIsOpen(false);
        setActiveSection(sectionId);
        isManualScrollRef.current = true;
        if (manualScrollTimerRef.current) clearTimeout(manualScrollTimerRef.current);
        manualScrollTimerRef.current = setTimeout(() => {
            isManualScrollRef.current = false;
        }, 1000);

        if (isLandingRoute) {
            const element = document.getElementById(sectionId);
            if (element) {
                element.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
            return;
        }

        navigate('/');
        let attempts = 0;
        const maxAttempts = 40;
        const tryScroll = () => {
            const el = document.getElementById(sectionId);
            if (el) {
                requestAnimationFrame(() => el.scrollIntoView({ behavior: 'smooth', block: 'start' }));
                return;
            }
            attempts += 1;
            if (attempts <= maxAttempts) {
                setTimeout(tryScroll, 100);
            }
        };
        setTimeout(tryScroll, 120);
    };

    useEffect(() => {
        if (!isLandingRoute) {
            setActiveSection('');
        }
    }, [isLandingRoute]);

    useEffect(() => {
        let rafId: number | null = null;
        const onScroll = () => {
            if (rafId) cancelAnimationFrame(rafId);
            rafId = requestAnimationFrame(() => {
                if (isManualScrollRef.current) return;
                let found = '';
                if (window.scrollY < 100) {
                    found = 'hero';
                }
                if (!found && (window.innerHeight + window.scrollY) >= document.documentElement.scrollHeight - 50) {
                    found = 'contact';
                }
                if (!found) {
                    for (const id of sectionIds) {
                        const el = document.getElementById(id);
                        if (!el) continue;
                        const rect = el.getBoundingClientRect();
                        if (rect.top <= 120 && rect.bottom > 120) {
                            found = id;
                            break;
                        }
                    }
                }
                if (!found) {
                    for (const id of sectionIds) {
                        const el = document.getElementById(id);
                        if (!el) continue;
                        const rect = el.getBoundingClientRect();
                        if (rect.top >= 0 && rect.top < window.innerHeight / 2) {
                            found = id;
                            break;
                        }
                    }
                }
                if (found) {
                    setActiveSection(found);
                }
            });
        };
        window.addEventListener('scroll', onScroll, { passive: true });
        onScroll();
        return () => {
            window.removeEventListener('scroll', onScroll);
            if (rafId) cancelAnimationFrame(rafId);
        };
    }, []);

    const handleLogout = React.useCallback(() => {
        let sessionId: string | undefined;
        try {
            sessionId = JSON.parse(localStorage.getItem('AUTHDATA') || '{}')?.sessionId;
        } catch { /* ignore */ }
        dispatch(authActions.logoutRequest({ sessionId }));

        localStorage.removeItem('AUTHDATA');
        localStorage.removeItem('SESSIONID');
        sessionStorage.removeItem('TS');
        setIsAuthenticated(false);
        setAuthUser(null);
        window.dispatchEvent(new Event('authStateChanged'));
        navigate('/login');
    }, [dispatch, navigate]);

    return (
        <nav className="fixed w-full z-50 bg-white/80 backdrop-blur-md border-b border-slate-100">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex justify-between h-16 items-center">
                    <div className="flex items-center gap-2">
                        <div className="flex items-center">
                            <img src={`${getblobLink()}/public/asset/logo_with_name.png`} alt="DriveMynt Logo" 
                                loading="lazy" width="180" height="180"
                            />  
                        </div>
                        <span className="text-xl font-bold tracking-tight text-slate-900"><span className="text-brand-primary"></span></span>
                    </div>
                    <div className="hidden md:flex items-center space-x-8">
                        <a onClick={() => scrollToSection('hero')} className={`text-sm font-medium transition-colors cursor-pointer ${activeSection === 'hero' ? 'text-brand-primary font-bold' : 'text-slate-600 hover:text-brand-primary'}`}>Home</a>
                        <a onClick={() => scrollToSection('about')} className={`text-sm font-medium transition-colors cursor-pointer ${activeSection === 'about' ? 'text-brand-primary font-bold' : 'text-slate-600 hover:text-brand-primary'}`}>About</a>
                        <a href="/pricing" className={`text-sm font-medium transition-colors ${isPricingRoute ? 'text-brand-primary font-bold' : 'text-slate-600 hover:text-brand-primary'}`}>Pricing</a>
                        <a onClick={() => scrollToSection('contact')} className={`text-sm font-medium transition-colors cursor-pointer ${activeSection === 'contact' ? 'text-brand-primary font-bold' : 'text-slate-600 hover:text-brand-primary'}`}>Contact</a>
                        {isAuthenticated ? (
                            <Dropdown
                                menu={{
                                    items: [
                                        {
                                            key: 'dashboard',
                                            label: <a href="/dashboard">Go to Dashboard</a>,
                                        },
                                        {
                                            key: 'profile',
                                            label: <a href="/profile">Profile</a>,
                                        },
                                        {
                                            type: 'divider',
                                        },
                                        {
                                            key: 'logout',
                                            danger: true,
                                            label: 'Logout',
                                            onClick: handleLogout,
                                        },
                                    ],
                                }}
                                trigger={['click']}
                                placement="bottomRight"
                            >
                                <button className="flex items-center gap-2 rounded-full transition-colors">
                                    <div className="w-9 h-9 rounded-full bg-slate-200 flex items-center justify-center text-sm font-semibold text-slate-800">
                                        {authUser?.firstName
                                            ? `${(authUser.firstName[0] || '').toUpperCase()}${authUser.lastName ? (authUser.lastName[0] || '').toUpperCase() : ''}`
                                            : authUser?.name
                                                ? authUser.name.split(' ').map((n: string) => n[0]).slice(0, 2).join('').toUpperCase()
                                                : 'U'}
                                    </div>

                                </button>
                            </Dropdown>
                        ) : (
                            <>
                                <a href="/login" className="text-sm font-medium text-slate-600 hover:text-brand-primary transition-colors ">Login</a>
                                <a href="/register" className="bg-brand-primary text-white px-5 py-2 rounded-full text-sm font-semibold bg-[var(--primary-color)] hover:bg-blue-700 transition-all shadow-lg shadow-blue-200">
                                    Register
                                </a>
                            </>
                        )}
                    </div>
                    <div className="md:hidden">
                        <button onClick={() => setIsOpen(!isOpen)} className="text-slate-600">
                            {isOpen ? <X /> : <Menu />}
                        </button>
                    </div>
                </div>
            </div>

            {isOpen && (
                <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} className="md:hidden bg-white border-b border-slate-100 px-4 py-6 space-y-4">
                    <button onClick={() => scrollToSection('hero')} className={`block w-full text-left text-base font-medium px-10 ${activeSection === 'hero' ? 'text-brand-primary font-bold' : 'text-slate-600'}`}>Home</button>
                    <button onClick={() => scrollToSection('features')} className={`block w-full text-left text-base font-medium px-10 ${activeSection === 'features' ? 'text-brand-primary font-bold' : 'text-slate-600'}`}>Features</button>
                    <button onClick={() => scrollToSection('about')} className={`block w-full text-left text-base font-medium px-10 ${activeSection === 'about' ? 'text-brand-primary font-bold' : 'text-slate-600'}`}>About</button>
                    <button onClick={() => scrollToSection('contact')} className={`block w-full text-left text-base font-medium px-10 ${activeSection === 'contact' ? 'text-brand-primary font-bold' : 'text-slate-600'}`}>Contact</button>

                    <a href="/pricing" id="pricing" className={`block text-base font-medium px-10 ${isPricingRoute ? 'text-brand-primary font-bold' : 'text-slate-600'}`}>Pricing</a>
                    {isAuthenticated ? (
                        <>
                            <a href="/dashboard" className="block w-full bg-brand-primary bg-[var(--primary-color)] text-white px-5 py-3 rounded-xl text-base font-semibold text-center">
                                Go to Dashboard
                            </a>
                            <button onClick={handleLogout} className="block w-full text-left text-base font-medium text-red-600 px-10 py-3">Logout</button>
                        </>
                    ) : (
                        <>
                            <a href="/login" className="block w-full text-left text-base font-medium text-slate-600 px-10">Login</a>
                            <a href="/register" className="block w-full bg-brand-primary bg-[var(--primary-color)] text-white px-5 py-3 rounded-xl text-base font-semibold text-center">
                                Register School
                            </a>
                        </>
                    )}
                </motion.div>
            )}
        </nav>
    );
};

// ============================================
// HERO SECTION
// ============================================
const Hero = () => {

    const navigate = useNavigate();
    const onhandleTryFree = () => {
      const auth = localStorage.getItem('AUTHDATA');
        if (auth) {
            navigate('/dashboard');
        } else {
            navigate('/login');
        }
    }

    return (
        <section id="hero" className="relative pt-22 pb-4 overflow-hidden bg-white">
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
                <div className="absolute top-10 right-0 w-[500px] h-[500px] bg-[var(--primary-color-lighter,#EEF2FF)] rounded-full blur-3xl opacity-60" />
                <div className="absolute bottom-0 left-0 w-72 h-72 bg-emerald-50 rounded-full blur-3xl opacity-40" />
            </div>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
                <div className="grid lg:grid-cols-[1fr_1.4fr] gap-4 items-center min-h-[76vh] ">
                    {/* Left content */}
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                        className="text-center lg:text-left"
                    >
                        <h1 className="text-4xl sm:text-5xl lg:text-[3.4rem] xl:text-6xl font-bold text-slate-900 leading-tight mb-6">
                            Master Your <br /><span className="text-[var(--primary-color)]">Driving Skills,</span> <br />Quick &amp; Easy!
                        </h1>
                        <p className="text-lg text-slate-500 mb-8 max-w-lg mx-auto lg:mx-0">
                            The complete platform to manage your driving school. Student enrollment, batch scheduling, vehicle tracking, and more — all in one place.
                        </p>
                        <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                            <a onClick={onhandleTryFree}
                            className="inline-flex items-center justify-center gap-2 bg-[var(--primary-color)] text-white px-8 py-4 rounded-full text-lg font-semibold hover:bg-[var(--primary-color-dark,#4338CA)] transition-all shadow-lg shadow-[var(--primary-color-lighter,#EEF2FF)]">
                                Try free <ArrowRight className="w-5 h-5" /></a>
                        </div>
                    </motion.div>
                    <motion.div initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.8, delay: 0.2 }} className="relative -mr-8 lg:-mr-16">
                        <img className="w-full img-isometric" src={`${getblobLink()}/public/asset/drivingTestEnv.webp`} alt="Isometric Driving School" width="600" height="500" fetchPriority="high" />
                    </motion.div>
                </div>
            </div>
        </section>
    );
};

export { Navbar, Hero };
